import {
  useChangeUserPasswordMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
} from '../../query/users';
import { StaffSignupFormValues } from '../../types/users';
import { getUserFormData } from '../../utils/user';
import { useSite } from '../../context/SiteContext';
import { useCallback } from 'react';
import { errorToast, successToast } from 'utils/toast';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';

const GENERATE_RANDOM_PASSWORD_BE_KEY = 'GENERATE';

export const useStaffUserCreateUpdate = ({
  onFinished,
}: {
  onFinished?: () => unknown;
} = {}) => {
  const { t } = useCommonTranslation();
  const { mutateAsync: createUser, isLoading: isCreateUserLoading } =
    useCreateUserMutation();
  const { mutateAsync: updateUser, isLoading: isUpdateUserLoading } =
    useUpdateUserMutation();
  const { mutateAsync: updatePassword, isLoading: isPasswordLoading } =
    useChangeUserPasswordMutation();

  const { activeSite } = useSite();

  const staffUserCreate = useCallback(
    async (data: StaffSignupFormValues) => {
      const password = data.confirmPassword || GENERATE_RANDOM_PASSWORD_BE_KEY;
      const { userInfo } = getUserFormData({
        ...data,
        disabilities: 0,
        active_site_id: activeSite?.id,
      });
      await createUser({
        ...userInfo,
        role_id: data.role_id,
        active_site_id: userInfo.active_site_id!,
        password,
      });
      successToast(t('user-created-successfully'));
    },
    [createUser, activeSite, t],
  );

  const staffUserUpdate = useCallback(
    async (data: StaffSignupFormValues) => {
      const { userInfo } = getUserFormData({
        ...data,
        disabilities: 0,
        active_site_id: activeSite?.id,
      });
      await updateUser(userInfo);
      if (data.confirmPassword) {
        await updatePassword({
          id: userInfo.id!!,
          password: data.confirmPassword,
        });
      }
      successToast(t('user-updated-successfully'));
    },
    [activeSite, updatePassword, updateUser, t],
  );

  const staffUserCreateOrUpdate: (
    data: StaffSignupFormValues,
    isUpdateUser: boolean,
  ) => Promise<void> = async (data, isUpdateUser) => {
    try {
      let handledError = undefined;
      if (isUpdateUser) {
        handledError = await staffUserUpdate(data);
      } else {
        handledError = await staffUserCreate(data);
      }
      if (handledError === undefined && onFinished) {
        onFinished();
      }
    } catch (e) {
      errorToast(e);
    }
  };

  return {
    staffUserCreateOrUpdate,
    isLoading: isPasswordLoading || isCreateUserLoading || isUpdateUserLoading,
  };
};
